import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined"
import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import ScrollableTableContainer from "../../../common/components/tables/ScrollableTableContainer"
import { adminApi } from "../axios"
import AdminHeaderTableCell, {
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "./AdminHeaderTableCell"
import AdminTruncatedCell from "./AdminTruncatedCell"

function accountOptionLabel(option) {
  return option?.email ? `${option.name || option.email} (${option.email})` : ""
}

function StatItem({ label, children }) {
  return (
    <Column gap={0.25}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }} component="div">
        {children}
      </Typography>
    </Column>
  )
}

export default function AdminMlsDetailDialog({ open, mlsId, onClose }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [mls, setMls] = useState(null)

  const [accountOptions, setAccountOptions] = useState([])
  const [accountSearchLoading, setAccountSearchLoading] = useState(false)
  const [accountSearchQuery, setAccountSearchQuery] = useState("")
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [assignSaving, setAssignSaving] = useState(false)
  const [assignError, setAssignError] = useState("")
  const [removingUserId, setRemovingUserId] = useState(null)

  const loadDetail = useCallback(() => {
    if (!mlsId) return
    setLoading(true)
    setError("")
    adminApi
      .fetchMlsDetail(mlsId)
      .then(({ data }) => setMls(data))
      .catch(() => setError("Unable to load MLS details."))
      .finally(() => setLoading(false))
  }, [mlsId])

  useEffect(() => {
    if (!open || !mlsId) {
      return
    }
    setMls(null)
    setSelectedAccount(null)
    setAccountSearchQuery("")
    setAssignError("")
    loadDetail()
  }, [open, mlsId, loadDetail])

  useEffect(() => {
    if (!open) return undefined
    if (accountSearchQuery.length < 2) {
      setAccountOptions([])
      return undefined
    }
    const timer = setTimeout(() => {
      setAccountSearchLoading(true)
      adminApi
        .searchAccounts(accountSearchQuery)
        .then(({ data }) => setAccountOptions(data))
        .finally(() => setAccountSearchLoading(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [open, accountSearchQuery])

  function handleAssign() {
    if (!selectedAccount?.id || !mlsId) return
    setAssignSaving(true)
    setAssignError("")
    adminApi
      .assignMlsToUser(mlsId, selectedAccount.id)
      .then(() => {
        setSelectedAccount(null)
        setAccountSearchQuery("")
        loadDetail()
      })
      .catch(() => setAssignError("Unable to assign this user to the MLS."))
      .finally(() => setAssignSaving(false))
  }

  function handleRemove(user) {
    if (!mlsId) return
    setRemovingUserId(user.id)
    setAssignError("")
    adminApi
      .removeMlsFromUser(mlsId, user.id)
      .then(() => loadDetail())
      .catch(() => setAssignError("Unable to remove this user from the MLS."))
      .finally(() => setRemovingUserId(null))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{mls ? mls.name : "MLS details"}</DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        {loading ? (
          <Row sx={{ justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Row>
        ) : mls ? (
          <Column gap={2.5} sx={{ pt: 0.5 }}>
            <Paper
              variant="outlined"
              sx={{
                px: 2.5,
                py: 1.75,
                borderRadius: 1.5,
                bgcolor: "action.hover",
              }}
            >
              <Row gap={4} sx={{ flexWrap: "wrap", alignItems: "flex-start" }}>
                <StatItem label="MLS ID">
                  <Typography
                    component="span"
                    sx={{ fontFamily: "monospace", fontSize: "0.8125rem" }}
                  >
                    {mls.id}
                  </Typography>
                </StatItem>
                <StatItem label="Status">
                  <Chip
                    size="small"
                    label={mls.visible ? "Active" : "Inactive"}
                    color={mls.visible ? "success" : "default"}
                    variant="outlined"
                  />
                </StatItem>
                <StatItem label="Connected users">{mls.user_count}</StatItem>
              </Row>
            </Paper>

            <Column gap={1.25}>
              <Typography variant="subtitle2">Assign a user</Typography>
              {assignError ? (
                <Alert severity="error" onClose={() => setAssignError("")}>
                  {assignError}
                </Alert>
              ) : null}
              <Row gap={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Autocomplete
                  options={accountOptions}
                  loading={accountSearchLoading}
                  value={selectedAccount}
                  inputValue={accountSearchQuery}
                  onChange={(_, value) => {
                    setSelectedAccount(value)
                    setAccountSearchQuery(
                      value ? accountOptionLabel(value) : ""
                    )
                  }}
                  onInputChange={(_, value) => setAccountSearchQuery(value)}
                  getOptionLabel={accountOptionLabel}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  filterOptions={(x) => x}
                  sx={{ minWidth: 300, flex: 1, maxWidth: 440 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search by name or email"
                      size="small"
                      helperText="Minimum 2 characters"
                      fullWidth
                    />
                  )}
                />
                <Button
                  variant="contained"
                  disabled={!selectedAccount?.id || assignSaving}
                  onClick={handleAssign}
                >
                  {assignSaving ? "Assigning…" : "Assign"}
                </Button>
              </Row>
            </Column>

            <Divider />

            <Column gap={1}>
              <Typography variant="subtitle2">
                Connected users
                {mls.users && mls.users.length > 0
                  ? ` (${mls.users.length})`
                  : ""}
              </Typography>
              {mls.users && mls.users.length > 0 ? (
                <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                  <ScrollableTableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <AdminTableHeadRow>
                          <AdminHeaderTableCell minWidth={{ xs: 140, sm: 200 }}>
                            Name
                          </AdminHeaderTableCell>
                          <AdminHeaderTableCell minWidth={{ xs: 160, sm: 220 }}>
                            Email
                          </AdminHeaderTableCell>
                          <AdminHeaderTableCell
                            align="center"
                            minWidth={{ xs: 80, sm: 100 }}
                          >
                            MLS Paid
                          </AdminHeaderTableCell>
                          <AdminHeaderTableCell
                            align="right"
                            minWidth={{ xs: 72, sm: 90 }}
                          >
                            Remove
                          </AdminHeaderTableCell>
                        </AdminTableHeadRow>
                      </TableHead>
                      <TableBody>
                        {mls.users.map((user) => (
                          <TableRow key={user.id} hover>
                            <AdminTruncatedCell
                              value={`${user.name || "—"}${
                                user.is_active === false ? " (inactive)" : ""
                              }`}
                              limit={28}
                            />
                            <AdminTruncatedCell value={user.email} limit={30} />
                            <TableCell align="center" sx={adminTableBodyCellSx}>
                              {user.has_paid_for_mls ? (
                                <Row
                                  gap={0.5}
                                  sx={{
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <CheckCircleOutlineIcon
                                    fontSize="small"
                                    color="success"
                                  />
                                  <Typography
                                    variant="body2"
                                    color="success.main"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    Paid
                                  </Typography>
                                </Row>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={adminTableBodyCellSx}>
                              <Tooltip title="Remove from this MLS">
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={`Remove ${user.email} from MLS`}
                                    disabled={removingUserId === user.id}
                                    onClick={() => handleRemove(user)}
                                  >
                                    {removingUserId === user.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <PersonRemoveOutlinedIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollableTableContainer>
                </Paper>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No users connected to this MLS.
                </Typography>
              )}
            </Column>
          </Column>
        ) : (
          <Alert severity="error">{error || "MLS not found."}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
